import React, { useState, useMemo } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Eye,
  Power,
  PowerOff,
  Trash2,
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  Building2,
  TrendingUp,
} from "lucide-react";

export default function AdminResellers() {
  const [search, setSearch] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const { t, language } = useLanguage();

  const statusConfig = useMemo(() => ({
    ACTIVE: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: t("active") },
    SUSPENDED: { icon: XCircle, color: "bg-red-100 text-red-700", label: t("suspended") },
    INACTIVE: { icon: Clock, color: "bg-amber-100 text-amber-700", label: t("inactive") },
  }), [t, language]);
  const queryClient = useQueryClient();

  const { data: resellers = [], isLoading } = useQuery({
    queryKey: ["admin-resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["all-merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateReseller(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-resellers"]);
      setStatusDialog(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminService.deleteReseller(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-resellers"]);
      setDeleteDialog(null);
    },
  });

  const filteredResellers = resellers.filter(r =>
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getMerchantCount = (resellerId) => {
    return merchants.filter(m => m.reseller_id === resellerId).length;
  };

  const getMonthlyVolume = (resellerId) => {
    // Mock calculation
    return Math.floor(Math.random() * 10000000) + 500000;
  };

  const handleToggleStatus = async () => {
    if (!statusDialog) return;
    const newStatus = statusDialog.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({
      id: statusDialog.id,
      data: { name: statusDialog.name, status: newStatus },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("resellers")}</h1>
          <p className="text-slate-500">{resellers.length} {t("resellerPartners")}</p>
        </div>
        <Link to={createPageUrl("AdminResellerForm")}>
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            {t("createReseller")}
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t("searchResellers")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">{t("reseller")}</TableHead>
              <TableHead className="font-semibold">ID</TableHead>
              <TableHead className="font-semibold">{t("parent")}</TableHead>
              <TableHead className="font-semibold">{t("status")}</TableHead>
              <TableHead className="font-semibold">{t("merchants")}</TableHead>
              <TableHead className="font-semibold">{t("monthlyVolume")}</TableHead>
              <TableHead className="font-semibold">{t("created")}</TableHead>
              <TableHead className="font-semibold w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  {t("loading")}
                </TableCell>
              </TableRow>
            ) : filteredResellers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  {t("resellers")}
                </TableCell>
              </TableRow>
            ) : (
              filteredResellers.map((reseller, index) => {
                const status = statusConfig[reseller.status] || statusConfig.INACTIVE;
                const StatusIcon = status.icon;
                const merchantCount = getMerchantCount(reseller.id);
                const monthlyVolume = getMonthlyVolume(reseller.id);

                return (
                  <motion.tr
                    key={reseller.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.02 * index }}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{reseller.name}</p>
                          <p className="text-sm text-slate-500">{reseller.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{reseller.id.substring(0, 8)}</Badge>
                    </TableCell>
                    <TableCell>
                      {reseller.createdByEngineId ? (
                        <Badge variant="outline" className="border-sky-300 text-sky-700 bg-sky-50">
                          {t("engine")}: {engines.find(e => e.id === reseller.createdByEngineId)?.name || reseller.createdByEngineId.substring(0, 8)}
                        </Badge>
                      ) : reseller.createdByAdminId ? (
                        <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50">
                          {t("directAdmin")}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Unknown</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${status.color} gap-1 border-0`}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-400" />
                        <span>{merchantCount}</span>
                        {reseller.max_merchants && (
                          <span className="text-slate-400">/ {reseller.max_merchants}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-emerald-500" />
                        <span className="font-semibold">฿{monthlyVolume.toLocaleString()}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {reseller.createdAt ? format(new Date(reseller.createdAt), "MMM d, yyyy") : "N/A"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={createPageUrl("AdminResellerDetail") + `?id=${reseller.id}`}>
                              <Eye className="h-4 w-4 mr-2" />
                              {t("viewDetails")}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={createPageUrl("AdminResellerForm") + `?id=${reseller.id}`}>
                              <Pencil className="h-4 w-4 mr-2" />
                              {t("edit")}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setStatusDialog(reseller)}>
                            {reseller.status?.toUpperCase() === "ACTIVE" ? (
                              <>
                                <PowerOff className="h-4 w-4 mr-2" />
                                {t("suspend")}
                              </>
                            ) : (
                              <>
                                <Power className="h-4 w-4 mr-2" />
                                {t("activate")}
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteDialog(reseller)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t("delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </motion.tr>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Status Toggle Dialog */}
      <AlertDialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusDialog?.status === "active" ? t("suspendReseller") : t("activateReseller")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusDialog?.status === "active"
                ? `${t("areYouSure")} ${t("suspend")} "${statusDialog?.name}"? ${t("merchantsWillBeAffected")}`
                : `${t("areYouSure")} ${t("activate")} "${statusDialog?.name}"?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleStatus}
              className={statusDialog?.status === "active" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
            >
              {statusDialog?.status === "active" ? t("suspend") : t("activate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteReseller")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirmation")} "{deleteDialog?.name}"? {t("cannotBeUndone")}
              {t("merchantsBecomeOrphaned")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteDialog.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}